import { Component, OnInit, inject } from '@angular/core';
import { AuthService } from './core/services/auth.service';
import { BusinessCentralSyncListenerService } from './core/services/business-central-sync-listener.service';
import { shareReplay, take, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from './core/constants';
import { NgParticlesService } from '@tsparticles/angular';
import { ISourceOptions, Main, OutMode } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { ThemeService } from './core/services/theme.service';
import { environment } from 'src/environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  private translateService = inject(TranslateService);
  private particlesService = inject(NgParticlesService);
  authService = inject(AuthService);
  private router = inject(Router);
  private businessCentralSyncListener = inject(BusinessCentralSyncListenerService);

  public isAuthRoute = false;

  private readonly authRoutePrefixes = [
    '/login',
    '/registration',
    '/forgot-password',
    '/reset-password',
    '/change-password'
  ];

  public readonly particlesOptions: ISourceOptions = {
    particles: {
      number: { value: 80, density: { enable: true, width: 1100, height: 1100 } },
      color: { value: environment.dotsColor },
      shape: {
        type: ['circle', 'polygon'],
        options: {
          polygon: {
            sides: 5
          }
        }
      },
      opacity: {
        value: 0.5,
        animation: {
          enable: false
        }
      },
      size: {
        value: { min: 0.1, max: 3 },
        animation: {
          enable: false
        }
      },
      links: {
        enable: true,
        distance: 150,
        color: environment.dotsColor,
        opacity: 0.4,
        width: 1,
      },
      move: {
        enable: true,
        speed: 2.5,
        direction: 'none',
        random: false,
        straight: false,
        outModes: { default: OutMode.out },
        attract: { enable: false, rotate: { x: 600, y: 1200 } },
      },
    },
    interactivity: {
      detectsOn: 'canvas',
      events: {
        onHover: { enable: true, mode: 'repulse' },
        onClick: { enable: true, mode: 'push' },
        resize: { enable: true },
      },
      modes: {
        grab: { distance: 400, links: { opacity: 1 } },
        bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 },
        repulse: { distance: 200, duration: 0.4 },
        push: { particlesNb: 4 },
        remove: { particlesNb: 2 },
      },
    },
    detectRetina: true
  };

  public user$ = this.authService.user$.pipe(shareReplay(1));

  constructor() {
    const themeService = inject(ThemeService);

    this.translateService.setDefaultLang(Constants.DefaultLanguage);
    const language = window.localStorage.getItem('interfaceLanguage');

    if (language) {
      this.translateService.use(language);
    } else {
      this.translateService.use(Constants.DefaultLanguage);
    }

    themeService.setTheme();
  }

  ngOnInit(): void {
    this.particlesService.init(async (engine: Main) => await loadSlim(engine));

    this.updateAuthRouteFlag(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .pipe(tap((event) => this.updateAuthRouteFlag(event.urlAfterRedirects)))
      .subscribe();

    this.initBusinessCentralSyncListener();
  }

  // AppComponent is the single app-root, so initializing the BC sync listener here
  // guarantees exactly one app-lifetime subscription, unlike the multi-mounted
  // <app-navbar-profile-info>. Wait for the first authenticated token before init.
  private initBusinessCentralSyncListener(): void {
    this.authService.user$
      .pipe(
        filter((user) => user.loggedIn && !!user.accessToken),
        take(1),
        tap((user) => this.businessCentralSyncListener.init(user.accessToken)),
      )
      .subscribe();
  }

  public switchLanguage(lang: string): void {
    this.translateService.use(lang);
  }

  private updateAuthRouteFlag(url: string): void {
    this.isAuthRoute = this.authRoutePrefixes.some((prefix) => url.startsWith(prefix));
  }
}
